/**
 * Returns the URL to the API.
 */
import { assertEnvVariable } from '../environment';
import { getViteEnv } from '../environment/vite-env';
import { isDefined } from '../variables';

function ensureTrailingSlash(url: string) {
  return url.endsWith('/') ? url : `${url}/`;
}

export function resolveApiUrl() {
  const domainFromVite =
    getViteEnv('DOMAIN') ?? getViteEnv('VITE_DOMAIN') ?? getViteEnv('VITE_API_URL');

  if (!isDefined(domainFromVite)) {
    return `https://${assertEnvVariable('DOMAIN') ?? ''}/`;
  }

  if (!domainFromVite || domainFromVite.trim().length === 0) {
    throw new Error('API URL is not set');
  }

  const normalized = domainFromVite.trim().replace(/\/$/, '');
  if (/^https?:\/\//i.test(normalized)) {
    return ensureTrailingSlash(normalized);
  }
  return `https://${normalized}/`;
}

/**
 * Returns the URL to the internal API.
 */
export function resolveInternalApiUrl() {
  return `https://${assertEnvVariable('INTERNAL_API_DOMAIN') ?? ''}/`.replace(/\/+$/, '/');
}

/**
 * Returns URL to the public web address.
 */
export function resolvePublicUrl() {
  return `https://${assertEnvVariable('PUBLIC') ?? ''}/`;
}

/**
 *  Returns the xsite integration domain.
 *
 *  TODO: Handle in a more dynamic way.
 */
export const resolveXSiteIntegrationDomain = () => {
  return `https://${assertEnvVariable('XSITE_INTEGRATION_DOMAIN') ?? ''}/`;
};

/**
 *  Returns the Leica integration domain.
 *
 *  TODO: Handle in a more dynamic way.
 */
export const resolveLeicaIntegrationDomain = () => {
  return `https://${assertEnvVariable('LEICA_INTEGRATION_DOMAIN') ?? ''}/`;
};

/**
 *  Returns the Cerbos cluster url.
 */
export const resolveCerbosUrl = () => {
  // Env-variables existence verified using DotEnv-example file.
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  return `https://${assertEnvVariable('CERBOS_URL')}`;
};

/**
 *  Returns the issuer URL of the Zitadel instance that hosts the login page.
 *
 *  The same issuer serves every environment; the environments are told apart by their client id.
 */
export const resolveZitadelIssuer = () => {
  return assertEnvVariable('ZITADEL_ISSUER');
};

/**
 *  Returns the OIDC client id of this environment's Zitadel portal project.
 */
export const resolveZitadelClientId = () => {
  return assertEnvVariable('ZITADEL_CLIENT_ID');
};

/**
 *  Returns the id of this environment's Zitadel portal project.
 *
 *  Requested as an audience scope on login so the access token's `aud` carries it, which is what
 *  the backend pins to keep another environment's tokens out.
 */
export const resolveZitadelProjectId = () => {
  return assertEnvVariable('ZITADEL_PROJECT_ID');
};
